import React, { useState, useEffect } from 'react';
import { Loader, AlertCircle, CheckCircle, Copy, Check, Link2 } from 'lucide-react';
import { paymentLinksApi, installmentSettingsApi } from '../api/client';

const PAYMENT_METHODS = [
  { value: 'PIX', label: 'PIX' },
  { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
  { value: 'BOLETO', label: 'Boleto' },
];

const initialForm = {
  amount: '',
  method: 'PIX',
  installments: '',
  installmentMode: 'fixed',
  reason: '',
  dueDate: '',
  customerName: '',
  customerEmail: '',
  customerIdentification: '',
  customerPhone: '',
};

const GenerateLink = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [maxInstallments, setMaxInstallments] = useState(12);
  const [maxInstallmentsNoInterest, setMaxInstallmentsNoInterest] = useState(6);

  useEffect(() => {
    loadInstallmentSettings();
  }, []);

  const loadInstallmentSettings = async () => {
    try {
      const response = await installmentSettingsApi.getActive();
      if (response.data.success && response.data.data) {
        setMaxInstallments(response.data.data.max_installments || 12);
        setMaxInstallmentsNoInterest(response.data.data.interest_free_limit || 6);
      }
    } catch (err) {
      console.error('Failed to load installment settings, using defaults:', err);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const getDefaultBoletoDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    return date.toISOString().split('T')[0];
  };

  const handleMethodChange = (value) => {
    setForm((prev) => ({
      ...prev,
      method: value,
      dueDate: value === 'BOLETO' ? getDefaultBoletoDate() : '',
    }));
  };

  const isValidForSubmission = () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return false;
    if (!form.method) return false;

    if (form.method === 'BOLETO' && !form.customerIdentification.trim()) return false;
    if (form.method === 'BOLETO' && !form.customerEmail.trim()) return false;
    if (form.method === 'CREDIT_CARD' && !form.installments) return false;

    return true;
  };

  const resetForm = () => {
    setForm(initialForm);
    setResult(null);
    setError(null);
    setCopied(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidForSubmission()) return;

    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const hasCustomerData =
        form.customerName || form.customerEmail || form.customerIdentification || form.customerPhone;

      const payload = {
        amount: parseFloat(form.amount),
        method: form.method,
        reason: form.reason || undefined,
        installments: form.method === 'CREDIT_CARD' ? parseInt(form.installments, 10) : undefined,
        installmentMode: form.method === 'CREDIT_CARD' ? form.installmentMode : undefined,
        dueDate: form.method === 'BOLETO' ? form.dueDate : undefined,
        customer: hasCustomerData
          ? {
              name: form.customerName || undefined,
              email: form.customerEmail || undefined,
              identification: form.customerIdentification || undefined,
              phone: form.customerPhone || undefined,
            }
          : undefined,
      };

      const response = await paymentLinksApi.create(payload);
      setResult(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar o link de pagamento.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.paymentLinkUrl) return;
    try {
      await navigator.clipboard.writeText(result.paymentLinkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <div className="px-4 sm:px-0 max-w-3xl mx-auto">
      <div className="mb-8 flex items-center gap-3">
        <Link2 className="h-7 w-7 text-primary-500" />
        <div>
          <h1 className="text-3xl font-bold text-white">Gerar Link de Pagamento</h1>
          <p className="mt-1 text-sm text-gray-400">
            Crie um link de pagamento avulso, sem vínculo com nenhum pedido.
          </p>
        </div>
      </div>

      {result ? (
        <div className="bg-dark-700 shadow rounded-lg p-6 border border-dark-600">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="h-6 w-6 text-green-400" />
            <h2 className="text-lg font-medium text-white">Link gerado com sucesso</h2>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Link de Pagamento</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={result.paymentLinkUrl}
                  className="flex-1 border border-dark-500 bg-dark-800 text-white rounded-md py-2 px-3 text-sm"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-dark-900 bg-primary-500 hover:bg-primary-600"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="block text-xs text-gray-400">Método</span>
                <span className="text-white font-medium">
                  {PAYMENT_METHODS.find((m) => m.value === result.method)?.label || result.method}
                </span>
              </div>
              <div>
                <span className="block text-xs text-gray-400">Valor</span>
                <span className="text-white font-medium">R$ {parseFloat(result.amount).toFixed(2)}</span>
              </div>
              <div className="col-span-2">
                <span className="block text-xs text-gray-400">Referência</span>
                <span className="text-white font-mono text-xs break-all">{result.reference}</span>
              </div>
              {result.boletoBarcode && (
                <div className="col-span-2">
                  <span className="block text-xs text-gray-400">Código de Barras</span>
                  <span className="text-white font-mono text-xs break-all">{result.boletoBarcode}</span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center px-4 py-2 border border-dark-500 text-sm font-medium rounded-md text-gray-300 bg-dark-600 hover:bg-dark-500"
            >
              Gerar outro link
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-dark-700 shadow rounded-lg p-6 border border-dark-600">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300">Valor</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => updateField('amount', e.target.value)}
                  className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300">Método de Pagamento</label>
                <select
                  value={form.method}
                  onChange={(e) => handleMethodChange(e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-dark-500 bg-dark-700 text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-300">Motivo (opcional)</label>
                <input
                  type="text"
                  value={form.reason}
                  onChange={(e) => updateField('reason', e.target.value)}
                  className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Ex: Depósito cliente Fernanda"
                  maxLength={80}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Usado na referência do pagamento e para identificação interna.
                </p>
              </div>

              {form.method === 'BOLETO' && (
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300">Data de Vencimento</label>
                  <input
                    type="date"
                    value={form.dueDate}
                    onChange={(e) => updateField('dueDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              )}

              {form.method === 'CREDIT_CARD' && (
                <>
                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Parcelamento</label>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="installmentMode"
                          value="fixed"
                          checked={form.installmentMode === 'fixed'}
                          onChange={(e) => updateField('installmentMode', e.target.value)}
                          className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-500"
                        />
                        <span className="ml-2 text-sm text-gray-300">Parcelamento Fixo</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="installmentMode"
                          value="max"
                          checked={form.installmentMode === 'max'}
                          onChange={(e) => updateField('installmentMode', e.target.value)}
                          className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-500"
                        />
                        <span className="ml-2 text-sm text-gray-300">Até X Parcelas</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300">
                      {form.installmentMode === 'fixed' ? 'Número de Parcelas' : 'Máximo de Parcelas'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={maxInstallments}
                      value={form.installments}
                      onChange={(e) => updateField('installments', e.target.value)}
                      className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="1"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Até {maxInstallmentsNoInterest}x sem juros.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-dark-600 pt-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">
                Dados do Cliente {form.method === 'BOLETO' ? '(obrigatório para boleto)' : '(opcional)'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400">Nome</label>
                  <input
                    type="text"
                    value={form.customerName}
                    onChange={(e) => updateField('customerName', e.target.value)}
                    className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400">
                    E-mail {form.method === 'BOLETO' && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    type="email"
                    value={form.customerEmail}
                    onChange={(e) => updateField('customerEmail', e.target.value)}
                    className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    required={form.method === 'BOLETO'}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400">
                    CPF/CNPJ {form.method === 'BOLETO' && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    type="text"
                    value={form.customerIdentification}
                    onChange={(e) => updateField('customerIdentification', e.target.value)}
                    className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    required={form.method === 'BOLETO'}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400">Telefone</label>
                  <input
                    type="text"
                    value={form.customerPhone}
                    onChange={(e) => updateField('customerPhone', e.target.value)}
                    className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-900 bg-opacity-50 border border-red-700 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="ml-3 text-sm text-red-200">{error}</p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!isValidForSubmission() || submitting}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-dark-900 bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  'Gerar Link'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default GenerateLink;
